import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Typography, Input, Popconfirm, Card, Row, Col, Form } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import {
    GET_USERS,
    GET_SEARCHED_USER,
    DELETE_USER,
    GET_INVITED_USERS,
    RESEND_INVITATION,
    RESEND_INVITATIONS,
    DELETE_INVITATION,
} from 'src/graphql';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { IUserAccounts, IInvitedUsers } from '../interfaces';
import { errorMessage, successMessage } from '../utils/Notifications';
import Paginate from './Paginate';
import { PrimaryButton } from './index';

const { Title, Text } = Typography;
const FormItem = Form.Item;

type FormValues = {
    email: string;
    searchText: string;
};

export type UserTableProps = {
    title?: string;
    roleId?: 'CLIENT' | 'ADMIN';
};

function InvitedUsersTable({ title, roleId }: UserTableProps): JSX.Element {
    const navigate = useNavigate();
    const [resendInvitation] = useMutation(RESEND_INVITATION);
    const [deleteInvitation] = useMutation(DELETE_INVITATION);
    const [resendInvitations] = useMutation(RESEND_INVITATIONS);
    
    const [userAccounts, setUserAccounts] = useState<IInvitedUsers[]>([]);
    const [offset, setOffset] = useState(0);
    const [count, setCount] = useState(20);
    const [total, setTotal] = useState(0);

    const { loading: usersLoading, refetch } = useQuery(GET_INVITED_USERS, {
        fetchPolicy: 'network-only',
        variables: { count, offset },
        onCompleted: (data) => {
            setUserAccounts(data?.getInvitedUsers?.users as IInvitedUsers[]);
            setTotal(data.getInvitedUsers?.pagination?.total);
        },
    });

    const [fetchUser, { loading: searchUserLoading, data }] = useLazyQuery(GET_SEARCHED_USER);

    const searchUser = (values: FormValues) => {
        const { searchText } = values;
        fetchUser({
            variables: {
                roleId,
                searchText,
            },
        });
    };

    const handleDeleteInvitation = (record: IInvitedUsers) => {
        const userId = record.id;
        deleteInvitation({
            variables: {
                userId,
            },
        })
            .then(() => {
                refetch()
                    .then((res) => {
                        setUserAccounts(res.data?.getInvitedUsers?.users as IInvitedUsers[]);
                        setTotal(res.data?.getInvitedUsers?.pagination?.total);
                        successMessage('Invite deleted successfully');
                    })
                    .catch(() => {
                        errorMessage('error fetching data');
                    });
            })
            .catch(() => {
                errorMessage('Failed to delete user');
            });
    };

    const handleResendInvitation = (record: IInvitedUsers) => {
        const { email } = record;
        resendInvitation({
            variables: {
                email,
            },
        })
            .then(() => {
                refetch()
                    .then((res) => {
                        setUserAccounts(res.data?.getInvitedUsers?.users as IInvitedUsers[]);
                        setTotal(res.data?.getInvitedUsers?.pagination?.total);
                        successMessage('Invite sent successfully');
                    })
                    .catch(() => {
                        errorMessage('error fetching data');
                    });
            })
            .catch(() => {
                errorMessage('Failed to send invites');
            });
    };


    const handleResendInvitations = () => {
        resendInvitations({
            variables: {
                email: "all",
            },
        })
        .then(() => {
            successMessage('Invite sent successfully');
        })
        .catch(() => {
            errorMessage('Failed to resend invitations');
        });
    };

    const handleNext = (from: number) => {
        setOffset(from);
        refetch()
            .then((res) => {
                setUserAccounts(res.data?.getInvitedUsers?.users as IInvitedUsers[]);
                setTotal(res.data?.getInvitedUsers?.pagination?.total);
            })
            .catch(() => {
                errorMessage('error fetching data');
            });
    };

    useEffect(() => {
        if (data?.findUser) {
            setUserAccounts(data?.findUser?.users as IInvitedUsers[]);
        }
    }, [data]);

    const columns = [
        {
            key: 'email',
            title: 'Email',
            dataIndex: 'email',
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (record: IUserAccounts) => (
                <div className="display-flex">
                    <div style={{ marginLeft: '5px' }}>
                        <Popconfirm title="Sure to delete?" onConfirm={() => handleResendInvitation(record)}>
                            <DeleteOutlined className="hoverable-icon" />
                        </Popconfirm>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            <div
                style={{
                    padding: '7px',
                    marginLeft: '5px',
                    marginRight: '5px',
                    background: '#ffffff',
                    borderRadius: '15px',
                }}
            >
                <Row>
                    <Col span="24" style={{ paddingLeft: '10px', paddingTop: '10px' }}>
                        <Text className="card-heading">{title}</Text>
                    </Col>
                    <Col span="24" style={{ marginTop: '-27px', paddingLeft: '10px' }}>
                        <Text style={{ fontWeight: 'bold', fontSize: '20px', color: '#EE773B' }}>___</Text>
                    </Col>
                </Row>
                {roleId === 'ADMIN' && (
                    <Card>
                        <Row>
                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }}>
                                <Text>&nbsp;</Text>
                            </Col>
                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} style={{textAlign: "right"}}>
                            <Popconfirm
                                title="Are you sure you want to resend invitations?"
                                onConfirm={() => handleResendInvitations()}
                            >
                                 <PrimaryButton
                                    title="Resend Invitations"
                                    htmlType="submit"
                                    loading={searchUserLoading}
                                />
                            </Popconfirm>
                            </Col>
                        </Row>
                    </Card>
                )}
                <Card loading={usersLoading}>
                    <table width="100%">
                        <thead>
                            <th style={{ width: '80%', textAlign: 'left', fontSize: '17px' }}>Email</th>
                            <th style={{ width: '20%', textAlign: 'left', fontSize: '17px' }}>Actions</th>
                        </thead>
                        <tbody>
                            {userAccounts !== undefined &&
                                userAccounts.length > 0 &&
                                userAccounts.map((user) => {
                                    return (
                                        <tr
                                            style={{
                                                height: '50px',
                                                fontSize: '17px',
                                                color: '#0b543e',
                                                borderBottom: '1px solid #f4f4f4',
                                            }}
                                        >
                                            <td>{user.email}</td>
                                            <td>
                                                <div className="display-flex">
                                                    <div style={{ marginLeft: '5px' }}>
                                                        <Popconfirm
                                                            title="Sure to delete?"
                                                            onConfirm={() => handleDeleteInvitation(user)}
                                                        >
                                                            <DeleteOutlined className="hoverable-icon" />
                                                        </Popconfirm>
                                                    </div>
                                                    <div style={{ marginLeft: '5px' }}>
                                                        <Popconfirm
                                                            title="Are you sur you want to resend invitation?"
                                                            onConfirm={() => handleResendInvitation(user)}
                                                        >
                                                            Resend Invite
                                                        </Popconfirm>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </Card>
                <Card>
                    {userAccounts !== undefined && userAccounts.length > 0 && (
                        <Paginate pageCount={total / count} getRecords={handleNext} />
                    )}
                </Card>
            </div>
        </>
    );
}

export default InvitedUsersTable;
